import * as React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useCookies } from "react-cookie";
import { Authenticated } from "./Authenticated";
import UserContext from "./UserContext";
import { Form, FormControl, Button } from "react-bootstrap"
import { ApiHelper, LoginRequestInterface, LoginResponseInterface, UserHelper, ToastHelper } from "./helpers";

export const Login: React.FC = () => {
  const [cookies, setCookie] = useCookies();
  const location = useLocation();
  const navigate = useNavigate();
  const context = React.useContext(UserContext);

  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [loginError, setLoginError] = React.useState("");

  let search = new URLSearchParams(location.search);
  let jwt = search.get("jwt") || cookies.jwt;
  let guid = search.get("guid");
  const returnUrl = search.get("returnUrl") || "";
  const fromLocation = (location.state as any)?.from;
  const from = fromLocation ? (fromLocation.pathname + (fromLocation.search || "")) : "/";
  if (!jwt) jwt = "";
  if (!guid) guid = "";

  const init = () => {
    if (guid) login({ resetGuid: guid });
    else if (jwt) { login({ jwt: jwt }); }
  };


  React.useEffect(init, []); //eslint-disable-line

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    switch (e.currentTarget.id) {
      case "email": setEmail(e.currentTarget.value); break;
      case "password": setPassword(e.currentTarget.value); break;
    }
  }

  const login = async (req: LoginRequestInterface) => {
    setIsSubmitting(true);
    setLoginError("");
    try {
      const resp: LoginResponseInterface = await ApiHelper.postAnonymous("/users/login", req);
      if (!resp.user) {
        if (req.email) {
          const errorMessage = (resp.errors?.length > 0)
            ? resp.errors[0]
            : "Unable to sign in. Please try again later.";
          setLoginError(errorMessage);
          ToastHelper.error(errorMessage, 10000);
        }
        else window.location.href = "/logout";
      }
      else if (resp.departments.length === 0) {
        ToastHelper.error("User does not have access to any accounts.");
      }
      else {
        UserHelper.user = resp.user;
        UserHelper.departments = resp.departments;
        UserHelper.currentDepartment = resp.departments[0];
        //UserHelper.departmentChanged = true;
        ApiHelper.setDefaultPermissions(UserHelper.currentDepartment.jwt);
        ApiHelper.isAuthenticated = true;
        context.setUserName(UserHelper.currentDepartment.id.toString());
        setCookie("jwt", UserHelper.currentDepartment.jwt, { path: "/" });
        if (returnUrl) {
          navigate(returnUrl, { replace: true });
        } else if (from !== "/") {
          navigate(from, { replace: true });
        } else if (guid) {
          navigate("/", { replace: true });
        }
      }
    } catch {
      const errorMessage = "Unable to sign in. Please try again later.";
      setLoginError(errorMessage);
      ToastHelper.error(errorMessage, 10000);
    }
    setIsSubmitting(false);
  }

  const handleSubmit = (e: React.MouseEvent) => {
    let errors: string[] = [];
    if (email.length === 0) errors.push("Please enter an email address.");
    if (password.length === 0) errors.push("Please enter a password.");

    if (errors.length > 0) {
      errors.forEach(error => ToastHelper.error(error));
    } else {
      login({ email: email, password: password });
    }
  }

  const getLoginBox = () => {
    return (
      <div id="loginPage">
        <div id="loginBox" data-testid="login-box">
          <div className="login-logo">
            <img src="/images/logo.png" alt="SignPresenter" />
          </div>
          <h2>Welcome Back</h2>
          <p className="login-subtitle">Sign in to manage your digital signage</p>

          <Form.Group className="mb-3">
            <Form.Label htmlFor="email">Email Address</Form.Label>
            <FormControl
              type="email"
              aria-label="Email address"
              aria-required="true"
              id="email"
              data-testid="email-input"
              value={email}
              onChange={handleChange}
              placeholder="Enter your email"
              autoComplete="email"
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label htmlFor="password">Password</Form.Label>
            <FormControl
              type="password"
              aria-label="Password"
              aria-required="true"
              id="password"
              data-testid="password-input"
              value={password}
              onChange={handleChange}
              placeholder="Enter your password"
              autoComplete="current-password"
            />
          </Form.Group>
          {loginError && (
            <div className="alert alert-danger py-2 mb-3" role="alert" data-testid="login-error">
              {loginError}
            </div>
          )}
          <Button
            type="submit"
            id="signInButton"
            data-testid="sign-in-button"
            size="lg"
            variant="primary"
            disabled={isSubmitting}
            style={{ width: "100%" }}
            onClick={handleSubmit}
            aria-busy={isSubmitting}
          >
            {(isSubmitting) ? "Signing in..." : "Sign In"}
          </Button>

          <div className="login-links">
            <Link to="/forgot">Forgot your password?</Link>
          </div>

          <div className="login-divider">
            <span>New to SignPresenter?</span>
          </div>

          <a href="https://signpresenter.com/#signUp" className="btn btn-outline-secondary" style={{ width: "100%" }}>
            Create an Account
          </a>
        </div>
      </div>
    );
  }




  if (context.userName === "" || !ApiHelper.isAuthenticated) {
    return getLoginBox();
  } else {
    return <Authenticated />;
  }
};